import { Injectable } from '@angular/core';
import { ConnectionService } from '../connection-service';
import { WindowRefService } from '../window-ref-service';

type ParseUser = { id: string; get: (key: string) => unknown };

/**
 * Operators listed in the `operator_only_access` experiment variant (`{ "operators": [login email or username] }`)
 * must work only on operator.cureskin.com. On any other host they get logged out.
 */
@Injectable({ providedIn: 'root' })
export class OperatorAccessService {
  static readonly EXPERIMENT_KEY: string = 'operator_only_access';
  static readonly OPERATOR_HOST: string = 'operator.cureskin.com';
  static readonly CACHE_TTL_MS: number = 5 * 60 * 1000;

  private cache: { userId: string; blocked: boolean; fetchedAt: number } | undefined;

  constructor(private conn: ConnectionService, private windowRef: WindowRefService) {}

  async isBlockedOnThisHost(user: ParseUser | null | undefined): Promise<boolean> {
    if (!user || this.windowRef.nativeWindow.location.hostname === OperatorAccessService.OPERATOR_HOST) {
      return false;
    }
    if (this.cache?.userId === user.id && Date.now() - this.cache.fetchedAt < OperatorAccessService.CACHE_TTL_MS) {
      return this.cache.blocked;
    }
    const blocked: boolean = await this.isUserInExperiment(user);
    this.cache = { userId: user.id, blocked, fetchedAt: Date.now() };
    return blocked;
  }

  private async isUserInExperiment(user: ParseUser): Promise<boolean> {
    try {
      const experiment: { get: (key: string) => unknown } | undefined = await this.conn.findExperiment({
        where: { key: OperatorAccessService.EXPERIMENT_KEY, enable: true },
      });
      const variant: { operators?: Array<string> } = JSON.parse((experiment?.get('variant') as string) || '{}');
      const operators: Array<string> = (variant.operators || []).map((each: string): string => each.trim().toLowerCase());
      // Google login matches internal users on PatientEmail, so that is the operator's login email.
      return ['PatientEmail', 'username']
        .map((key: string): unknown => user.get(key))
        .some((value: unknown): boolean => typeof value === 'string' && operators.includes(value.toLowerCase()));
    } catch (error) {
      // Never lock users out because the experiment lookup failed.
      return false;
    }
  }
}
